/* Fit the opening gallery without resizing the document during mobile scrolling. */
(() => {
  'use strict';
  const mounted = new Map();
  const desktopQuery = matchMedia('(min-width:990px)');
  const property = '--ivori-available-media-height';
  const pixels = value => parseFloat(value) || 0;
  const layoutWidth = () => document.documentElement.clientWidth || innerWidth;
  const viewportHeight = () => window.visualViewport?.height || innerHeight;

  const mount = root => {
    if (mounted.has(root) || root.dataset.viewportGalleryReady) return;
    const gallery = root.querySelector('.story-gallery');
    const hero = root.querySelector('.story-hero');
    const thumbs = root.querySelector('.story-thumbnails');
    const sticky = root.querySelector('.story-sticky') || root.closest('ivori-product-page')?.querySelector('[data-ivori-sticky]');
    if (!gallery || !hero || !thumbs || !sticky) return;

    const controller = new AbortController();
    const options = { passive: true, signal: controller.signal };
    const previousValue = root.style.getPropertyValue(property);
    let frame = 0;
    let observer;
    let width = layoutWidth();
    let mobileViewport = viewportHeight();
    let mobileMeasured = false;
    let mobileLocked = scrollY > 2;
    root.dataset.viewportGalleryReady = 'true';

    const measure = () => {
      frame = 0;
      if (controller.signal.aborted || !root.isConnected) return;
      const desktop = desktopQuery.matches;
      const nextWidth = layoutWidth();
      if (nextWidth !== width) {
        width = nextWidth;
        mobileViewport = viewportHeight();
        mobileMeasured = false;
        mobileLocked = scrollY > 2;
      }
      // Once reading begins, header/sticky-bar resizes must not move content
      // above the reader. A real layout-width change starts a new measurement.
      if (!desktop && mobileMeasured && (mobileLocked || scrollY > 2)) return;
      if (!desktop && window.visualViewport?.scale > 1.01) return;

      const font = pixels(getComputedStyle(root).fontSize) || 16;
      const top = desktop
        ? hero.getBoundingClientRect().top + scrollY + pixels(getComputedStyle(hero).paddingTop)
        : gallery.getBoundingClientRect().top + scrollY;
      const rail = thumbs.getBoundingClientRect().height + pixels(getComputedStyle(thumbs).marginTop);
      const viewport = desktop ? viewportHeight() : mobileViewport;
      const budget = Math.floor(viewport - top - rail - sticky.getBoundingClientRect().height - font * .75);
      const value = `${Math.max(font * 7.5, budget)}px`;
      if (root.style.getPropertyValue(property) !== value) root.style.setProperty(property, value);
      if (!desktop) mobileMeasured = true;
    };

    const schedule = () => {
      if (!frame && !controller.signal.aborted) frame = requestAnimationFrame(measure);
    };
    const resize = () => {
      // Browser chrome, the keyboard and pinch-zoom can change viewport height
      // without changing page width. Keep the mobile gallery stable for these.
      if (desktopQuery.matches || layoutWidth() !== width || !mobileMeasured) schedule();
    };
    const lockMobile = () => {
      if (!desktopQuery.matches && scrollY > 2) mobileLocked = true;
    };
    const breakpoint = () => {
      mobileViewport = viewportHeight();
      mobileMeasured = false;
      mobileLocked = scrollY > 2;
      schedule();
    };
    const cleanup = () => {
      controller.abort();
      cancelAnimationFrame(frame);
      observer?.disconnect();
      delete root.dataset.viewportGalleryReady;
      if (previousValue) root.style.setProperty(property, previousValue);
      else root.style.removeProperty(property);
      mounted.delete(root);
    };
    mounted.set(root, cleanup);
    addEventListener('resize', resize, options);
    window.visualViewport?.addEventListener('resize', resize, options);
    addEventListener('scroll', lockMobile, options);
    addEventListener('pageshow', resize, options);
    addEventListener('load', schedule, { once: true, signal: controller.signal });
    desktopQuery.addEventListener('change', breakpoint, options);
    document.fonts?.ready.then(schedule);
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(schedule);
      [root.querySelector('.story-intro'), root.querySelector('.story-social-proof'), thumbs, sticky, document.querySelector('header')].filter(Boolean).forEach(el => observer.observe(el));
    }
    schedule();
  };

  const init = (scope = document) => {
    if (scope.matches?.('.ivori-regular-treatments')) mount(scope);
    scope.querySelectorAll('.ivori-regular-treatments').forEach(mount);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, cleanup] of mounted) {
      if (event.target === root || event.target.contains(root)) cleanup();
    }
  });
})();
